import { Component, Input } from '@angular/core'
import { Job } from '../../job'
import { UtilisationWorkItem } from './utilisation-work-item'
import { ChartTopic } from '../charts/chart-topic'
import { ChartDatapoint } from '../charts/chart-datapoint'
import { WorkItem } from '../../work-item'
import { ElderColors } from '../../../common/elder-colors'
import { Duration } from '@js-joda/core'
import { ElderTranslatePipe, IsoDurationPipe, JsonMap } from '@elderbyte/ngx-starter'
import { WorkCenterMeta } from '../../work-center'
import { BarChartModule, Color, ScaleType } from '@swimlane/ngx-charts'
import { LoggerFactory } from '@elderbyte/ts-logger'

@Component({
  selector: 'app-job-detail-utilisation',
  templateUrl: './job-detail-utilisation.component.html',
  styleUrls: ['./job-detail-utilisation.component.scss'],
  imports: [BarChartModule, ElderTranslatePipe, IsoDurationPipe],
})
export class JobDetailUtilisationComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly logger = LoggerFactory.getLogger(this.constructor.name)

  @Input()
  public groupBy: string | null = null

  @Input()
  public filterFn: (workItem: WorkItem) => boolean

  @Input()
  public yAxisLabel = 'ProductionLine'

  @Input()
  public xAxisLabel = 'WorkItems'

  protected multi: any[] = []

  protected showXAxis = true
  protected showYAxis = true
  protected gradient = false
  protected showLegend = false
  protected showXAxisLabel = true
  protected showYAxisLabel = true

  protected colorScheme: Color = {
    name: 'elderScheme',
    selectable: true,
    group: ScaleType.Ordinal,
    domain: ElderColors.blues.concat(ElderColors.golds),
  }

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this.filterFn = () => true
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set job(job: Job) {
    if (this.groupBy === 'productionLine') {
      this.groupByProductionLine(job)
    } else if (this.groupBy === 'workCenter') {
      this.groupByWorkCenter(job)
    }
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onSelect(event: any): void {
    this.logger.debug(event)
  }

  public millisToDuration(millis: number): string {
    return Duration.ofMillis(millis).toString()
  }

  public millisToHours(millis: number): number {
    return Duration.ofMillis(millis).toHours()
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private buildIdToDisplay(item: WorkItem): string {
    return item.workOrderId + ':' + item.posNr
  }

  private groupByProductionLine(job: Job): void {
    const productionLines: Map<string, UtilisationWorkItem[]> = new Map<
      string,
      UtilisationWorkItem[]
    >()
    for (const item of job?.initialAssignments?.workItems.filter((workItem) =>
      this.filterFn(workItem)
    )) {
      const productionLineId = job?.optimizedAssignments?.assignments[item.id]
      if (productionLineId != null) {
        const utilisationWorkItems = productionLines.get(productionLineId)
        if (utilisationWorkItems == null) {
          productionLines.set(productionLineId, [
            this.buildProductionLineUtilisationWorkItem(item, productionLineId),
          ])
        } else {
          utilisationWorkItems.push(
            this.buildProductionLineUtilisationWorkItem(item, productionLineId)
          )
        }
      }
    }
    this.multi = this.buildDataset(productionLines, job.initialAssignments.workCenterMetas)
  }

  private buildProductionLineUtilisationWorkItem(
    item: WorkItem,
    productionLineId: string
  ): UtilisationWorkItem {
    const maybeMakespan = item.possibleProductionLines.find(
      (ppl) => ppl.productionPath === productionLineId
    )?.makespan
    return new UtilisationWorkItem(
      this.buildIdToDisplay(item),
      !!maybeMakespan ? maybeMakespan : Duration.ZERO.toString()
    )
  }

  private groupByWorkCenter(job: Job): void {
    const workCenters: Map<string, UtilisationWorkItem[]> = new Map<string, UtilisationWorkItem[]>()
    for (const wi of job?.initialAssignments?.workItems.filter((workItem) =>
      this.filterFn(workItem)
    )) {
      const productionLineId = job?.optimizedAssignments?.assignments[wi.id]
      if (productionLineId != null) {
        const productionLine = wi.possibleProductionLines.find(
          (ppl) => ppl.productionPath === productionLineId
        )
        if (!!productionLine) {
          for (const wc of productionLine.workCenters) {
            const utilisationWorkItems = workCenters.get(wc.id)
            if (utilisationWorkItems == null) {
              workCenters.set(wc.id, [
                new UtilisationWorkItem(this.buildIdToDisplay(wi), wc.makespan),
              ])
            } else {
              utilisationWorkItems.push(
                new UtilisationWorkItem(this.buildIdToDisplay(wi), wc.makespan)
              )
            }
          }
        }
      }
    }
    this.multi = this.buildDataset(workCenters, job.initialAssignments.workCenterMetas)
  }

  private buildDataset(
    data: Map<string, UtilisationWorkItem[]>,
    wcMetas: JsonMap<WorkCenterMeta>
  ): any[] {
    const dataset = []
    for (const key of data.keys()) {
      const maybeValue = data.get(key)
      if (!!maybeValue) {
        const value = maybeValue.map(
          (uwi) => new ChartDatapoint(uwi.id, Duration.parse(uwi.makespan).toMillis())
        )
        dataset.push(new ChartTopic(this.getWorkCenterName(key, wcMetas), value))
      }
    }
    this.sortByUtilisation(dataset)
    return dataset
  }

  private getWorkCenterName(path: string, wcMetas: JsonMap<WorkCenterMeta>): string {
    const segments = path.split('!')
    const wcIds = segments.map((segment) => segment.split('.').find((wcId) => wcId != null))
    let name = ''
    wcIds.forEach((wcId) => {
      if (!!wcId) {
        name = name.concat(wcMetas[wcId].name + ' ')
      }
    })
    return name
  }

  private sortByUtilisation(dataset: ChartTopic[]): void {
    dataset.sort((ct1, ct2) => this.getTotalValue(ct2.series) - this.getTotalValue(ct1.series))
  }

  private getTotalValue(datapoints: ChartDatapoint[]): number {
    return datapoints.map((s) => s.value).reduce((v1, v2) => v1 + v2)
  }
}
